import { LineString, Point, Polygon } from 'ol/geom'
import { Fill, Stroke, Style } from 'ol/style.js'
import CircleStyle from 'ol/style/Circle'
import { Feature } from 'ol'
import { FeatureLike } from 'ol/Feature'
import { ToolEnum } from '../model/tool.enum'
import { Icon } from 'ol/style'
import OlMap from 'ol/Map'

export class StyleUtils {
    private static panelCanvasCache: HTMLCanvasElement | null = null
    private static styleCache: Map<string, Style> = new Map()
    private static fixedLineStyleCache: Map<string, Style> = new Map()
    private static fixedPointStyleCache: Map<string, Style> = new Map()

    private static buildPanelCanvas(): HTMLCanvasElement {
        if (StyleUtils.panelCanvasCache) return StyleUtils.panelCanvasCache

        const size = 15
        const canvas = document.createElement('canvas')
        canvas.width = size
        canvas.height = size
        const ctx = canvas.getContext('2d')

        ctx.fillStyle = 'rgba(0, 0, 0, 0.5)'
        ctx.fillRect(0, 0, size, size)

        ctx.strokeStyle = 'rgba(255, 255, 255, 1)'
        ctx.lineWidth = 0.5

        ctx.beginPath()
        ctx.moveTo(0, size / 2)
        ctx.lineTo(size, size / 2)
        ctx.stroke()

        ctx.beginPath()
        ctx.moveTo(size / 2, 0)
        ctx.lineTo(size / 2, size)
        ctx.stroke()

        StyleUtils.panelCanvasCache = canvas
        return canvas
    }

    static computePolygonEdgeAngle(polygon: Polygon): number {
        const ring = polygon.getLinearRing(0)
        if (!ring) return 0
        const coords = ring.getCoordinates()
        if (coords.length < 2) return 0

        const dx = coords[1][0] - coords[0][0]
        const dy = coords[1][1] - coords[0][1]
        return Math.atan2(dy, dx)
    }

    static computeLineAngle(line: LineString): number {
        const coords = line.getCoordinates()
        if (coords.length < 2) return 0
        const first = coords[0]
        const last = coords[coords.length - 1]
        return Math.atan2(last[1] - first[1], last[0] - first[0])
    }

    static buildPanelStyleForAngle(strokeColor: string, angleRad: number): Style {
        const angleDeg = Math.round(-angleRad * 180 / Math.PI)
        const key = `${strokeColor}_${angleDeg}`
        if (StyleUtils.styleCache.has(key)) {
            return StyleUtils.styleCache.get(key)
        }

        const canvas = StyleUtils.buildPanelCanvas()
        const tmpCanvas = document.createElement('canvas')
        tmpCanvas.width = 1
        tmpCanvas.height = 1
        const ctx = tmpCanvas.getContext('2d')
        const pattern = ctx.createPattern(canvas, 'repeat')
        const matrix = new DOMMatrix()
        matrix.rotateSelf(0, 0, angleDeg)
        pattern.setTransform(matrix)

        const style = new Style({
            fill: new Fill({ color: pattern as any }),
            stroke: new Stroke({ color: strokeColor, width: 5 }),
            image: new CircleStyle({
                radius: 7,
                fill: new Fill({ color: strokeColor }),
                stroke: new Stroke({ color: strokeColor, width: 2 })
            })
        })
        StyleUtils.styleCache.set(key, style)
        return style
    }

    static buildRotatedPanelStyleFunction(strokeColor: string): (feature: FeatureLike) => Style {
        return (feature: FeatureLike): Style => {
            const geom = feature.getGeometry()
            if (geom && (geom.getType() === 'Polygon' || geom.getType() === 'MultiPolygon')) {
                const angle = StyleUtils.computePolygonEdgeAngle(geom as Polygon)
                return StyleUtils.buildPanelStyleForAngle(strokeColor, angle)
            }
            return StyleUtils.buildPanelStyleForAngle(strokeColor, 0)
        }
    }

    static buildPanelStyle(strokeColor: string): Style {
        return StyleUtils.buildPanelStyleForAngle(strokeColor, 0)
    }

    static buildDefaultStyleFromColor(color: string): Style {
        return new Style({
            fill: new Fill({
                color: color

            }),
            stroke: new Stroke({
                color: 'rgba(0, 0, 0, 0.5)',
                lineDash: [10, 10],
                width: 5
            }),
            image: new CircleStyle({
                radius: 5,
                stroke: new Stroke({
                    color: 'rgba(0, 0, 0, 0.7)'
                }),
                fill: new Fill({
                    color: color
                })
            })
        })
    }

    static buildAzimuthArrowStyle(feature: Feature): Style[] {
        const styles: Style[] = []
        const geometryType = feature.getGeometry().getProperties()['type']

        if (geometryType === ToolEnum.Azimuth) {
            (feature.getGeometry() as LineString).forEachSegment(function(start, end) {
                const dx = end[0] - start[0]
                const dy = end[1] - start[1]
                const rotation = Math.atan2(dy, dx)
                // arrow
                styles.push(
                    new Style({
                        geometry: new Point(end),
                        image: new Icon({
                            src: 'assets/images/img/right-arrow-angle.svg',
                            anchor: [0.75, 0.5],
                            rotateWithView: true,
                            rotation: -rotation,
                            scale: 0.05
                        }),
                    })
                )
            })
        }
        return styles
    }

    static buildFixedScreenLineStyleFunction(
        strokeColor: string,
        strokeWidth: number
    ): (feature: FeatureLike) => Style {
        return (): Style => {
            const cacheKey = `${strokeColor}_${strokeWidth}`
            const style = StyleUtils.fixedLineStyleCache.get(cacheKey) ?? new Style({
                stroke: new Stroke({
                    color: strokeColor,
                    width: strokeWidth,
                    lineCap: 'round',
                    lineJoin: 'round',
                }),
            })
            if (!StyleUtils.fixedLineStyleCache.has(cacheKey)) {
                StyleUtils.fixedLineStyleCache.set(cacheKey, style)
            }
            return style
        }
    }

    static buildFixedPointStyle(fillColor: string, radius: number, strokeColor: string, strokeWidth: number): Style {
        const cacheKey = `${fillColor}_${radius}_${strokeColor}_${strokeWidth}`
        const style = StyleUtils.fixedPointStyleCache.get(cacheKey) ?? new Style({
            image: new CircleStyle({
                radius,
                fill: new Fill({ color: fillColor }),
                stroke: new Stroke({ color: strokeColor, width: strokeWidth })
            }),
        })
        if (!StyleUtils.fixedPointStyleCache.has(cacheKey)) {
            StyleUtils.fixedPointStyleCache.set(cacheKey, style)
        }
        return style
    }

    static buildFixedPointStyleFunction(
        fillColor: string,
        radius: number,
        strokeColor: string,
        strokeWidth: number,
        map: OlMap
    ): (feature: FeatureLike, resolution: number) => Style {
        return (feature: FeatureLike, resolution: number): Style => {
            const zoom = map.getView().getZoom() ?? 19
            const baseZoom = 19
            const scale = Math.pow(2, zoom - baseZoom)
            const adjustedRadius = Math.max(2, radius * scale)

            return new Style({
                image: new CircleStyle({
                    radius: adjustedRadius,
                    fill: new Fill({ color: fillColor }),
                    stroke: new Stroke({ color: strokeColor, width: strokeWidth })
                }),
            })
        }
    }
}

